import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { Clock, CheckCircle, XCircle, AlertCircle, Loader2, FileText } from 'lucide-react';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

interface Execution {
  id: string;
  eventId?: string;
  status: 'success' | 'failure' | 'timeout';
  duration: number;
  error?: string;
  createdAt: string;
  completedAt?: string;
  logs?: Array<{
    level: string;
    message: string;
    data?: any;
  }>;
}

interface ExecutionHistoryProps {
  integrationId: string;
  onViewLogs?: (eventId: string) => void;
}

export function ExecutionHistory({ integrationId, onViewLogs }: ExecutionHistoryProps) {
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchExecutions = async (offset = 0) => {
    try {
      const response = await deiterateAPI.GET(
        `/v1/integrations/${integrationId}/executions?limit=20&offset=${offset}`,
        {
          backend: 'thalassa'
        }
      );
      
      const data = response;
      
      if (offset === 0) {
        setExecutions(data.executions || []);
      } else {
        setExecutions(prev => [...prev, ...(data.executions || [])]);
      }
      
      setHasMore((data.executions || []).length === 20);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchExecutions();
  }, [integrationId]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case 'failure':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'timeout':
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      success: 'default',
      failure: 'destructive',
      timeout: 'secondary',
    } as const;

    return (
      <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (executions.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">No executions found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {executions.map((execution) => (
        <Card key={execution.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  {getStatusIcon(execution.status)}
                  {getStatusBadge(execution.status)}
                  <span className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(execution.createdAt), { addSuffix: true })}
                  </span>
                </div>
                
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Duration:</span>{' '}
                    {execution.duration}ms
                  </p>
                  {execution.error && (
                    <p className="text-sm text-destructive">
                      <span className="font-medium">Error:</span> {execution.error}
                    </p>
                  )}
                </div>

                {execution.eventId && onViewLogs && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewLogs(execution.eventId!)}
                    className="mt-2"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View Logs
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => fetchExecutions(executions.length)}
          >
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}
